import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { getProductionPage, getMediaUrl, followUser } from '../services/api';
import './Production.css';

function Production() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const name = searchParams.get('name');
  const company = searchParams.get('company');

  const [production, setProduction] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectedIds, setConnectedIds] = useState(new Set());

  useEffect(() => {
    if (name) loadProduction();
  }, [name, company]);

  const loadProduction = async () => {
    setLoading(true);
    try {
      const res = await getProductionPage(name, company);
      const data = res.data;
      setProduction(data.production);
      setPeople(data.people || []);
    } catch (err) {
      console.error('Error loading production:', err);
    }
    setLoading(false);
  };

  const handleConnect = async (userId) => {
    try {
      await followUser(userId);
      setConnectedIds((prev) => new Set([...prev, userId]));
    } catch (err) {
      console.error('Error connecting:', err);
    }
  };

  if (loading) {
    return (
      <div className="production-page">
        <div className="production-loading">Loading production...</div>
      </div>
    );
  }

  if (!production) {
    return (
      <div className="production-page">
        <div className="production-empty">
          <h2>Production not found</h2>
          <p>No one on Showcase has credits for this production yet.</p>
        </div>
      </div>
    );
  }

  // Group people by role type
  const cast = people.filter(
    (p) =>
      ![
        'Director',
        'Choreographer',
        'Music Director',
        'Stage Manager',
        'Production Stage Manager',
        'Assistant Stage Manager',
        'Lighting Designer',
        'Set Designer',
        'Costume Designer',
        'Sound Designer',
        'Dramaturg',
      ].includes(p.role)
  );
  const creative = people.filter((p) =>
    ['Director', 'Choreographer', 'Music Director', 'Dramaturg'].includes(p.role)
  );
  const crew = people.filter((p) =>
    [
      'Stage Manager',
      'Production Stage Manager',
      'Assistant Stage Manager',
      'Lighting Designer',
      'Set Designer',
      'Costume Designer',
      'Sound Designer',
    ].includes(p.role)
  );

  const PersonRow = ({ person }) => (
    <div className="production-person">
      <Link to={`/profile/${person.user_id}`} className="production-person-link">
        <div className="production-person-avatar">
          {person.profile_image ? (
            <img src={getMediaUrl(person.profile_image)} alt="" />
          ) : (
            person.full_name?.charAt(0) || '?'
          )}
        </div>
        <div className="production-person-info">
          <div className="production-person-name">{person.full_name}</div>
          <div className="production-person-role">{person.role}</div>
          {person.professions_str && (
            <div className="production-person-profession">{person.professions_str}</div>
          )}
        </div>
      </Link>
      <button
        className={`production-connect-btn ${connectedIds.has(person.user_id) ? 'connected' : ''}`}
        onClick={() => handleConnect(person.user_id)}
        disabled={connectedIds.has(person.user_id)}
      >
        {connectedIds.has(person.user_id) ? 'Connected' : 'Connect'}
      </button>
    </div>
  );

  return (
    <div className="production-page">
      <div className="production-hero">
        <div className="production-hero-inner">
          <button className="production-back-link" onClick={() => navigate(-1)}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <div className="production-hero-card">
            <h1 className="production-title">{production.name}</h1>
            <div className="production-meta">
              {production.company && (
                <span className="production-company">{production.company}</span>
              )}
              {production.year && (
                <>
                  <span className="production-meta-divider">·</span>
                  <span className="production-year">{production.year}</span>
                </>
              )}
              {production.venue && (
                <>
                  <span className="production-meta-divider">·</span>
                  <span className="production-venue">{production.venue}</span>
                </>
              )}
            </div>
            <div className="production-credits-row">
              {production.director && (
                <span className="production-credit-tag">Dir: {production.director}</span>
              )}
              {production.choreographer && (
                <span className="production-credit-tag">Choreo: {production.choreographer}</span>
              )}
              {production.music_director && (
                <span className="production-credit-tag">MD: {production.music_director}</span>
              )}
              {production.playwright && (
                <span className="production-credit-tag">By: {production.playwright}</span>
              )}
              {production.casting_director && (
                <span className="production-credit-tag">
                  Casting: {production.casting_director}
                </span>
              )}
            </div>
            <div className="production-people-count">
              {production.people_count} {production.people_count === 1 ? 'person' : 'people'} on
              Showcase
            </div>
          </div>
        </div>
      </div>

      <div className="production-content">
        {cast.length > 0 && (
          <div className="production-section">
            <h2 className="production-section-title">Cast</h2>
            <div className="production-people-list">
              {cast.map((p) => (
                <PersonRow key={p.user_id} person={p} />
              ))}
            </div>
          </div>
        )}

        {creative.length > 0 && (
          <div className="production-section">
            <h2 className="production-section-title">Creative Team</h2>
            <div className="production-people-list">
              {creative.map((p) => (
                <PersonRow key={p.user_id} person={p} />
              ))}
            </div>
          </div>
        )}

        {crew.length > 0 && (
          <div className="production-section">
            <h2 className="production-section-title">Production Team</h2>
            <div className="production-people-list">
              {crew.map((p) => (
                <PersonRow key={p.user_id} person={p} />
              ))}
            </div>
          </div>
        )}

        {cast.length === 0 && creative.length === 0 && crew.length === 0 && people.length > 0 && (
          <div className="production-section">
            <h2 className="production-section-title">Company</h2>
            <div className="production-people-list">
              {people.map((p) => (
                <PersonRow key={p.user_id} person={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Production;
